import { inject, Injectable } from '@angular/core';
import {
  ITabPanelHeader,
  TabViewHeaderSize
} from '@design-system/components/m-tab-view';
import {
  ApiService,
  CampaignType,
  FiltersType,
  ICampaignsCount,
  IFilterCriteria,
  IUpdateCampaignTacticWithAiRequestModel,
  IUpdateCampaignWithFilterCriteria,
  UserType
} from '@libs/apis';
import { IPTableColumnInfo, ITitle, ViewType } from '@libs/common-components';
import { cloneDeep, isEmpty, isEqual, isNil, sortBy, uniqBy } from 'lodash-es';
import { AiCampaignsViewConstants } from '../components/ai-campaigns-view/ai-campaigns-view.constants';
import { Constants as CampaignsViewConstants } from '../components/campaigns-view/campaigns-view.constants';
import { TaxonomyHelper } from '../helper/taxonomy-helper';
import {
  ICampaignFilterContext,
  TaxonomyDateRange,
  TaxonomyDimension
} from '../models/ICampaignFilterContext';
import { ICampaignsCountByDimension } from '../models/ICampaignsCountByDataSource';
import { IColumnFilterContext } from '../models/IColumnFilterContext';
import { TaxonomyFeature } from '../models/TaxonomyFeature';
import { TaxonomyTab } from '../models/TaxonomyTab';
import { CampaignDimension } from '../taxonomy-ui/taxonomy-ui.constants';
import { CampaignAccordionService } from './campaign-accordion.service';
import { CampaignTableService } from './campaign-table.service';
import { CampaignViewService } from './campaign-view.service';
import { CampaignsConfirmationPopupService } from './campaigns-confirmation-popup.service';
import { CampaignsGlobalLoaderService } from './campaigns-global-loader.service';
import { SharedDataService } from './shared-data.service';
import { TableDataService } from './table-data.service';
import { ToastMessageService } from './toast-message.service';
import { TaxonomyAction } from '../models/TaxonomyAction';

@Injectable({
  providedIn: 'root'
})
export class AiCampaignsViewService {
  public clientId = 0;
  public brandId = 0;
  public userType!: UserType;
  public selectedDateRange: TaxonomyDateRange = TaxonomyDateRange.last30Days;
  public groupByDimension: TaxonomyDimension =
    TaxonomyDimension.compositeDataSourceName;

  public activeAccordionDimensionValue = '';
  public activeTaxonomyTab: TaxonomyTab = TaxonomyTab.unmapped;
  public filterCriteria: IFilterCriteria[] = [];
  public campaignTabHeaders: ITabPanelHeader[] =
    CampaignsViewConstants.tabPanelHeaders;

  public headerSize: TabViewHeaderSize = TabViewHeaderSize.default;
  public campaignCountsByType: Record<string, ICampaignsCountByDimension> = {};
  private campaignCountsByTypeActual: Record<
    string,
    ICampaignsCountByDimension
  > = {};

  public filterContext: ICampaignFilterContext = {
    ...CampaignsViewConstants.defaultFilterContext
  };

  public filterStateByTab: Record<string, IColumnFilterContext> = {};
  public geoTestStatusByDimension: Record<string, boolean> = {};
  public dateRangeOptions = CampaignsViewConstants.dateRangeOptions;

  public titleConfigs: Record<TaxonomyTab & TaxonomyFeature.AI, ITitle[]> = {
    [TaxonomyTab.unmapped]: AiCampaignsViewConstants.unmappedTitleConfigs,
    [TaxonomyTab.mapped]: AiCampaignsViewConstants.mappedTitleConfigs,
    [TaxonomyTab.underProcessing]:
      AiCampaignsViewConstants.underProcessingTitleConfigs,
    [TaxonomyFeature.AI]: AiCampaignsViewConstants.aiRecommendationsTitleConfigs
  };

  public activeTabIndex = 0;
  public campaignsMappedByAi: number | null = null;
  private updateDimensionValues!: Record<CampaignDimension, string>;
  public showLearnMoreModal = false;
  public defaultCampaignTableColumns!: IPTableColumnInfo[];

  readonly campaignViewService = inject(CampaignViewService);
  readonly campaignAccordionService = inject(CampaignAccordionService);
  readonly campaignTableService = inject(CampaignTableService);
  readonly sharedDataService = inject(SharedDataService);
  readonly toastMessageService = inject(ToastMessageService);
  readonly campaignsGlobalLoaderService = inject(CampaignsGlobalLoaderService);
  readonly apiService = inject(ApiService);
  readonly tableDataService = inject(TableDataService);
  readonly campaignsConfirmationPopupService = inject(
    CampaignsConfirmationPopupService
  );

  public initializeComponent(): Promise<void> {
    this.campaignAccordionService.accordionStateByCampaignType[
      this.activeTaxonomyTab
    ].loading = true;
    this.campaignAccordionService.accordionStateByCampaignType[
      TaxonomyFeature.AI
    ].loading = true;
    this.activeTaxonomyTab = TaxonomyTab.unmapped;
    this.campaignViewService.getGeoTestStatusByDimension(
      this.clientId,
      this.brandId
    );
    this.sharedDataService
      .getGeoTestStatusByDimension()
      .subscribe((geoTestStatusByDimension) => {
        this.geoTestStatusByDimension = geoTestStatusByDimension;
        this.campaignTableService.geoTestStatusByDimension = cloneDeep(
          geoTestStatusByDimension
        );
        this.campaignAccordionService.geoTestStatusByDimension = cloneDeep(
          geoTestStatusByDimension
        );
      });
    this.resetAiCampaignsView();
    this.initializeChildComponents();
    this.getCampaignsMappedByAi();
    return this.getCampaignCount();
  }

  public initializeChildComponents(): void {
    this.initializeCampaignTableService();
    this.initializeCampaignAccordionService();
  }

  private initializeCampaignAccordionService(): void {
    this.campaignAccordionService.viewFor = TaxonomyFeature.AI;
    this.campaignAccordionService.clientId = this.clientId;
    this.campaignAccordionService.brandId = this.brandId;

    this.campaignAccordionService.activeTaxonomyTab = this.activeTaxonomyTab;
    this.campaignAccordionService.accordions = [];
    this.campaignAccordionService.groupByDimension =
      this.filterContext?.groupByDimension;
    this.campaignAccordionService.geoTestStatusByDimension = {};
  }

  private initializeCampaignTableService(): void {
    this.campaignTableService.viewFor = TaxonomyFeature.AI;
    this.campaignTableService.clientId = this.clientId;
    this.campaignTableService.brandId = this.brandId;
    this.campaignTableService.activeTab = this.activeTaxonomyTab;
    this.campaignTableService.campaignDimensionInfo = null;
  }

  public publishTabChange(activeTab: number): void {
    if (this.activeTabIndex === activeTab) {
      return;
    }
    this.activeTaxonomyTab = Object.values(TaxonomyTab)[
      activeTab
    ] as TaxonomyTab;
    this.activeTabIndex = activeTab;
    this.resetCampaignsTable();
  }

  handleDateRangeChange(event: { value: TaxonomyDateRange }): void {
    this.selectedDateRange = event.value;
    this.campaignCountsByType = {};
    this.tableDataService.reset(`${this.clientId}-${this.brandId}`);
    this.getCampaignCount();
  }

  public getCampaignCount(): Promise<void> {
    this.campaignAccordionService.accordionStateByCampaignType[
      this.activeTaxonomyTab
    ].loading = true;
    if (this.activeTaxonomyTab === TaxonomyTab.unmapped) {
      this.campaignAccordionService.accordionStateByCampaignType[
        TaxonomyFeature.AI
      ].loading = true;
    }
    return this.campaignViewService
      .getCampaignCountWithFilter(
        this.selectedDateRange,
        this.filterContext.groupByDimension,
        this.activeAccordionDimensionValue,
        undefined,
        [],
        true
      )
      .then((campaignCountsByType) => {
        this.campaignCountsByType =
          TaxonomyHelper.enrichCampaignCountsData(campaignCountsByType);
        this.campaignCountsByTypeActual = cloneDeep(this.campaignCountsByType);
        requestAnimationFrame(() => {
          this.enrichTabPanelHeaders();
        });
        this.campaignAccordionService.accordionStateByCampaignType[
          this.activeTaxonomyTab
        ].loading = false;
        this.campaignAccordionService.accordionStateByCampaignType[
          TaxonomyFeature.AI
        ].loading = false;
        this.prepareDefaultCampaignTableColumns();
        return Promise.resolve();
      })
      .catch((error) => {
        console.error('Error occured while fetching campaigns', error);
        this.campaignAccordionService.accordionStateByCampaignType[
          this.activeTaxonomyTab
        ].loading = false;
        this.campaignAccordionService.accordionStateByCampaignType[
          TaxonomyFeature.AI
        ].loading = false;
        return Promise.reject(error);
      });
  }

  private prepareDefaultCampaignTableColumns(): void {
    this.defaultCampaignTableColumns = [
      ...CampaignsViewConstants.defaultDataSourceCampaignHeaders,
      CampaignsViewConstants.adsetNameColumn,
      ...CampaignsViewConstants.defaultCampaignHeadersByDimension[
        TaxonomyDimension.compositeDataSourceName
      ],
      ...CampaignsViewConstants.defaultCampaignHeadersByDimension[
        this.filterContext.groupByDimension
      ]
    ];
    const modifiedByColumnIndex = this.defaultCampaignTableColumns.findIndex(
      (column) => column.field === 'modifiedBy'
    );
    this.defaultCampaignTableColumns = uniqBy(
      this.defaultCampaignTableColumns,
      'field'
    );
    if (modifiedByColumnIndex > -1) {
      this.defaultCampaignTableColumns[modifiedByColumnIndex] =
        CampaignsViewConstants.mappedByColumn;
    } else {
      this.defaultCampaignTableColumns.push(
        CampaignsViewConstants.mappedByColumn
      );
    }
  }

  public async refreshCampaignRecords(
    value: number,
    isAiRecommended = false
  ): Promise<void> {
    if (isNil(value)) {
      await this.campaignTableService.selectOrunselectAllCampaigns(false);
      this.resetCampaignsTable();
      return;
    }
    if (
      this.campaignAccordionService.accordionStateByCampaignType[
        isAiRecommended ? TaxonomyFeature.AI : this.activeTaxonomyTab
      ].activeAccordionIndex !== value
    ) {
      this.resetCampaignsTable();
    }

    const tab = isAiRecommended
      ? AiCampaignsViewConstants.aiRecommended
      : this.activeTaxonomyTab;
    this.activeAccordionDimensionValue =
      this.campaignCountsByType[tab]?.metrics[value]?.dimension || '';
    this.campaignAccordionService.accordionStateByCampaignType[
      isAiRecommended ? TaxonomyFeature.AI : this.activeTaxonomyTab
    ].activeAccordionIndex = value;
    this.campaignTableService.loading = true;
    this.campaignTableService.filterContext = this.filterContext;
    this.campaignTableService.filterContext.dateRange = this.selectedDateRange;
    this.campaignTableService.filterContext.groupByDimension =
      this.filterContext.groupByDimension;
    this.campaignTableService.activeTab = this.activeTaxonomyTab;
    this.campaignTableService.campaignDimensionInfo = this.campaignCountsByType[
      tab
    ]?.metrics[value] as ICampaignsCount;
    this.campaignTableService.isAiRecommendedDataRequested = isAiRecommended;
    this.campaignTableService.viewFor = TaxonomyFeature.AI;
    this.prepareDefaultCampaignTableColumns();
    this.campaignTableService.loadCampaignsWithLazyLoading();
  }

  public refreshCampaignView(
    context: ICampaignFilterContext,
    tab: TaxonomyTab | 'aiRecommended'
  ): void {
    const filterContextCopy = cloneDeep(this.filterContext);
    this.filterContext = context;
    if (
      filterContextCopy.groupByDimension !== this.filterContext.groupByDimension
    ) {
      this.resetAiCampaignsView();
      this.getCampaignCount();
    } else {
      this.filterCampaignAccordions(tab);
    }
    if (
      !isEqual(
        sortBy(filterContextCopy.tableColumns, 'field'),
        sortBy(this.filterContext.tableColumns, 'field')
      )
    ) {
      if (isNil(this.campaignTableService.filterContext)) {
        this.campaignTableService.filterContext = {} as ICampaignFilterContext;
      }
      this.campaignTableService.filterContext.tableColumns =
        this.filterContext.tableColumns;
      this.campaignTableService.prepareAndCollateWithFilterContextTableColumns();
    }
  }

  private filterCampaignAccordions(tab: TaxonomyTab | 'aiRecommended'): void {
    if (
      this.filterContext.dimensionFilter &&
      !isEmpty(this.filterContext.dimensionFilter)
    ) {
      this.campaignCountsByType[tab] = {
        total: this.campaignCountsByTypeActual[tab]?.total || 0,
        metrics:
          (this.campaignCountsByTypeActual[tab]?.metrics || [])
            .filter((metric) =>
              metric.dimension
                ?.toLowerCase()
                .includes(
                  (this.filterContext.dimensionFilter || '').toLowerCase()
                )
            )
            .map((metric) => {
              metric.isFilterApplied = !isEmpty(
                this.filterStateByTab[
                  this.getColumnFilterStateKey(metric.dimension)
                ]?.filterCriteria
              );
              return metric;
            }) || []
      };
    } else {
      this.campaignCountsByType[tab] = this.campaignCountsByTypeActual[tab] || {
        total: 0,
        metrics: []
      };
    }
  }

  private getColumnFilterStateKey(activeAccordion?: string): string {
    return `${this.activeTaxonomyTab}-${activeAccordion ?? this.activeAccordionDimensionValue}`;
  }

  private enrichTabPanelHeaders() {
    const updatedHeaders = this.campaignViewService.setTotalCountForTabs(
      this.campaignCountsByType
    );
    if (this.campaignTabHeaders.length === updatedHeaders.length) {
      updatedHeaders.forEach((updatedTab: ITabPanelHeader, index: number) => {
        if (this.campaignTabHeaders[index]) {
          const currentTab =
            this.campaignTabHeaders[index] ||
            ({
              count: 0,
              showCount: false,
              disabled: false
            } as ITabPanelHeader);
          currentTab.count = updatedTab.count;
          currentTab.showCount = true;
          currentTab.disabled = updatedTab.disabled;
        }
      });
    } else {
      this.campaignTabHeaders = [...updatedHeaders];
    }
  }

  private resetAiCampaignsView(): void {
    this.activeAccordionDimensionValue = '';
    this.filterCriteria = [];
    this.campaignTabHeaders.forEach((header) => {
      header.count = 0;
      header.showCount = true;
      header.disabled = false;
    });
    this.headerSize = TabViewHeaderSize.default;
    this.campaignCountsByType = {};
    this.filterStateByTab = {};
    this.geoTestStatusByDimension = {};
    this.dateRangeOptions = CampaignsViewConstants.dateRangeOptions;
    this.campaignAccordionService.accordionStateByCampaignType[
      this.activeTaxonomyTab
    ].activeAccordionIndex = -1;
    this.campaignAccordionService.accordionStateByCampaignType[
      TaxonomyFeature.AI
    ].activeAccordionIndex = -1;
  }

  private resetCampaignsTable(): void {
    this.campaignAccordionService.accordionStateByCampaignType[
      this.activeTaxonomyTab
    ].activeAccordionIndex = -1;
    this.campaignAccordionService.accordionStateByCampaignType[
      TaxonomyFeature.AI
    ].activeAccordionIndex = -1;
    this.campaignAccordionService.accordionStateByCampaignType[
      TaxonomyFeature.AI
    ].loading = false;
    this.campaignAccordionService.accordionStateByCampaignType[
      this.activeTaxonomyTab
    ].loading = false;
    this.campaignTableService.selectedCampaigns = [];
    this.campaignTableService.campaignDimensionInfo = null;
    this.campaignTableService.campaignQueue.flush();
  }

  private getDataSourceIdForAiRecommendations(): string | undefined {
    if (
      this.filterContext.groupByDimension ===
      TaxonomyDimension.compositeDataSourceName
    ) {
      return this.activeAccordionDimensionValue;
    } else {
      return undefined;
    }
  }

  private getFilterCriteriaForAiRecommendations(): IFilterCriteria[] {
    if (this.campaignTableService.allCampaignsSelected) {
      const filterCriteria: IFilterCriteria[] = [];

      filterCriteria.push({
        fieldName: this.filterContext.groupByDimension,
        value: this.activeAccordionDimensionValue,
        filter: FiltersType.EQUALS
      });

      if (this.campaignTableService.isFilterContextApplied()) {
        filterCriteria.push(
          ...TaxonomyHelper.prepareGetCampaignFilterCriteria(
            this.campaignTableService.filterStateByTab[
              this.campaignTableService.getColumnFilterStateKey(
                this.activeAccordionDimensionValue
              )
            ] as IColumnFilterContext
          )
        );
      }

      return filterCriteria;
    }

    // If specific campaigns are selected, use temp record IDs
    return [
      ...TaxonomyHelper.prepareFilterCriteriaWithRecordId(
        this.campaignTableService.selectedCampaigns
      ),
      {
        fieldName: this.filterContext.groupByDimension,
        value: this.activeAccordionDimensionValue,
        filter: FiltersType.EQUALS
      }
    ];
  }

  private prepareApplyAiRecommendationsBody(
    dimensionValues?: Record<CampaignDimension, string>,
    approveAll = false
  ): IUpdateCampaignTacticWithAiRequestModel {
    const body: IUpdateCampaignTacticWithAiRequestModel = {
      campaignType: this.activeTaxonomyTab as unknown as CampaignType,
      dataSourceId: this.getDataSourceIdForAiRecommendations(),
      filterCriteria: approveAll
        ? []
        : this.getFilterCriteriaForAiRecommendations(),
      ...(dimensionValues || {})
    };

    // Add groupByDimension and dimensionValue if applicable
    if (this.filterContext.groupByDimension) {
      body.groupByDimension = this.filterContext.groupByDimension;
      body.dimensionValue = this.activeAccordionDimensionValue;
    }

    return body;
  }

  public applyAIRecommendationsMapping(
    dimensionValues?: Record<CampaignDimension, string>,
    approveAll = false
  ): void {
    if (
      !this.campaignTableService.selectedCampaigns.length &&
      !this.campaignTableService.allCampaignsSelected &&
      !approveAll
    ) {
      this.toastMessageService.showError(
        'No campaigns selected',
        'Please select campaigns to apply AI recommendations'
      );
      return;
    }

    const postBody = this.prepareApplyAiRecommendationsBody(
      dimensionValues,
      approveAll
    );
    const loaderMessage = this.getLoaderMessageForAction();
    this.campaignsGlobalLoaderService.showGlobalLoader(
      `We're on it!`,
      loaderMessage,
      ViewType.loader
    );
    this.apiService.taxonomyApisOperations
      .updateCampaignTacticWithAiRecommendation(
        this.clientId,
        this.brandId,
        postBody
      )
      .then(async () => {
        await this.apiService.taxonomyApisOperations.publishCampaignChanges(
          this.clientId,
          this.brandId
        );
        const toastMessage = this.getToastMessageForUpdate();
        this.toastMessageService.showSuccess(
          toastMessage.message,
          toastMessage.title
        );

        this.campaignTableService.filterStateByTab[
          this.campaignTableService.getColumnFilterStateKey(
            this.activeAccordionDimensionValue
          )
        ] = this.campaignTableService.getDefaultColumnFilterState(
          this.activeAccordionDimensionValue
        );

        setTimeout(() => {
          this.campaignTableService.reviewUndoMessage.message = '';
        }, 10000);

        this.getCampaignCount();
        this.getCampaignsMappedByAi();
        this.campaignTableService.selectedCampaigns = [];
        await this.tableDataService.reset(
          this.campaignTableService.keyForActiveTabAndDimension
        );
        await this.tableDataService.reset(
          `${this.clientId}-${this.brandId}-${TaxonomyTab.underProcessing}`
        );
        const index = this.campaignCountsByType[
          this.activeTaxonomyTab
        ]?.metrics.findIndex(
          (metric) => metric.dimension === this.activeAccordionDimensionValue
        );
        if (index && index > -1) {
          this.campaignAccordionService.accordionStateByCampaignType[
            this.activeTaxonomyTab
          ].activeAccordionIndex = index;
          this.campaignTableService.fetchAndPopulateCampaignsTable();
        } else {
          this.campaignAccordionService.accordionStateByCampaignType[
            this.activeTaxonomyTab
          ].activeAccordionIndex = -1;
          this.resetCampaignsTable();
        }
      })
      .catch((error) => {
        console.error('Error applying AI recommendations: ', error);
        this.toastMessageService.showError(
          'Something went wrong!',
          'Failed to apply AI recommendations'
        );
      })
      .finally(() => {
        this.campaignsGlobalLoaderService.hideGlobalLoader();
      });
  }

  public navigateToInProgressCampaigns(): void {
    this.activeTaxonomyTab = TaxonomyTab.underProcessing;
    const inprogressTabIndex = Object.values(TaxonomyTab).indexOf(
      TaxonomyTab.underProcessing
    );
    this.publishTabChange(inprogressTabIndex);
  }

  private prepareUpdateCampaignBody(
    updateDimensionValues: Record<CampaignDimension, string>
  ): IUpdateCampaignWithFilterCriteria {
    const body = {
      campaignType: this.activeTaxonomyTab as unknown as CampaignType,
      ...updateDimensionValues,
      brandId: this.brandId,
      filterCriteria: this.getFilterCriteria(),
      groupByDimension: this.filterContext.groupByDimension,
      dimensionValue: this.activeAccordionDimensionValue,
      isAiCompatible: true
    } as IUpdateCampaignWithFilterCriteria;

    if (
      this.filterContext.groupByDimension ===
      TaxonomyDimension.compositeDataSourceName
    ) {
      body.dataSourceId = this.activeAccordionDimensionValue;
    }

    return body;
  }

  public updateCampaignDimensionMapping(
    updateDimensionValues: Record<CampaignDimension, string>
  ): void {
    const postBody = this.prepareUpdateCampaignBody(updateDimensionValues);
    const loaderMessage = this.getLoaderMessageForAction();
    this.campaignsGlobalLoaderService.showGlobalLoader(
      `We're on it!`,
      loaderMessage,
      ViewType.loader
    );

    this.apiService.taxonomyApisOperations
      .updateCampaignsMapping(this.clientId, this.brandId, postBody)
      .then(async () => {
        await this.apiService.taxonomyApisOperations.publishCampaignChanges(
          this.clientId,
          this.brandId
        );
        const toastMessage = this.getToastMessageForUpdate();
        this.toastMessageService.showSuccess(
          toastMessage.message,
          toastMessage.title
        );

        this.campaignTableService.filterStateByTab[
          this.campaignTableService.getColumnFilterStateKey(
            this.activeAccordionDimensionValue
          )
        ] = this.campaignTableService.getDefaultColumnFilterState(
          this.activeAccordionDimensionValue
        );

        await this.getCampaignCount();
        this.getCampaignsMappedByAi();
        await this.tableDataService.reset(
          this.campaignTableService.keyForActiveTabAndDimension
        );
        const index = this.campaignCountsByType[
          this.activeTaxonomyTab
        ]?.metrics.findIndex(
          (metric) => metric.dimension === this.activeAccordionDimensionValue
        );
        if (index && index > -1) {
          this.campaignAccordionService.accordionStateByCampaignType[
            this.activeTaxonomyTab
          ].activeAccordionIndex = index;
          this.campaignTableService.fetchAndPopulateCampaignsTable();
        } else {
          this.campaignAccordionService.accordionStateByCampaignType[
            this.activeTaxonomyTab
          ].activeAccordionIndex = -1;
          this.resetCampaignsTable();
        }
      })
      .catch((error) => {
        console.error('Error : ', error);
        this.toastMessageService.showError(
          'Something went wrong!',
          'Failed to perform the update'
        );
      })
      .finally(() => {
        this.campaignsGlobalLoaderService.hideGlobalLoader();
      });
  }

  private getFilterCriteria(): IFilterCriteria[] {
    if (this.campaignTableService.allCampaignsSelected) {
      return this.prepareFilterCriteriaForSelectAllCampaigns();
    }
    return [
      {
        fieldName: this.filterContext.groupByDimension,
        value: this.activeAccordionDimensionValue,
        filter: FiltersType.EQUALS
      },
      ...TaxonomyHelper.prepareFilterCriteriaWithRecordId(
        this.campaignTableService.selectedCampaigns
      )
    ];
  }

  private prepareFilterCriteriaForSelectAllCampaigns(): IFilterCriteria[] {
    const filterCriteria: IFilterCriteria[] = [];
    filterCriteria.push({
      fieldName: this.filterContext.groupByDimension,
      value: this.activeAccordionDimensionValue,
      filter: FiltersType.EQUALS
    });

    if (this.campaignTableService.isFilterContextApplied()) {
      const filterContext =
        this.campaignTableService.filterStateByTab[
          this.campaignTableService.getColumnFilterStateKey(
            this.activeAccordionDimensionValue
          )
        ];
      filterCriteria.push(
        ...TaxonomyHelper.prepareGetCampaignFilterCriteria(
          filterContext as IColumnFilterContext
        )
      );
    }

    return filterCriteria;
  }

  private getLoaderMessageForAction(): string {
    const campaignType = this.campaignsConfirmationPopupService.campaignType;
    const action = this.campaignsConfirmationPopupService.action;
    const allCampaignsSelected =
      this.campaignsConfirmationPopupService.allCampaignsSelected;
    const campaignCount = this.campaignTableService.getSelectedCampaignCount();
    const campaignText = allCampaignsSelected
      ? 'all campaigns'
      : `${campaignCount.toLocaleString()} ${TaxonomyHelper.getCampaignString(
          campaignCount
        )}`;

    switch (campaignType) {
      case CampaignType.MAPPED_CAMPAIGN:
        if (action === 'edit') {
          return `Updating mapping for ${campaignText}...`;
        } else {
          // unmap action
          return `Unmapping ${campaignText}...`;
        }
      case CampaignType.UNMAPPED_CAMPAIGN:
        return `Approving mapping for ${campaignText}...`;
      case CampaignType.UNDER_PROCESSING_CAMPAIGN:
        if (action === 'edit') {
          return `Modifying mapping for ${campaignText}...`;
        } else {
          // unmap action
          return `Unmapping ${campaignText}...`;
        }
      case TaxonomyFeature.AI:
        if (action === 'approveAll') {
          return `Approving mapping for all campaigns under 'AI Recommendations'...`;
        } else {
          return `Applying AI recommendations to ${campaignText}...`;
        }
      default:
        return `Processing ${campaignText}...`;
    }
  }

  private getToastMessageForUpdate(): {
    title: string;
    message: string;
  } {
    const campaignType = this.campaignsConfirmationPopupService.campaignType;
    const action = this.campaignsConfirmationPopupService.action;
    const allCampaignsSelected =
      this.campaignsConfirmationPopupService.allCampaignsSelected;
    const campaignCount = this.campaignTableService.getSelectedCampaignCount();
    const campaignText = allCampaignsSelected
      ? 'all campaigns'
      : `${campaignCount.toLocaleString()} ${TaxonomyHelper.getCampaignString(
          campaignCount
        )}`;

    switch (campaignType) {
      case CampaignType.MAPPED_CAMPAIGN:
        if (action === 'edit') {
          return {
            title: `Campaign mapping updated successfully`,
            message: `The mapping for ${campaignText} has been updated. Changes will take effect in the next 2-3 hours.`
          };
        } else {
          // unmap action
          return {
            title: `Campaigns unmapped successfully`,
            message: `${campaignText} ${allCampaignsSelected ? 'have' : 'has'} been unmapped. Changes will take effect in the next 2-3 hours.`
          };
        }
      case CampaignType.UNMAPPED_CAMPAIGN:
        return {
          title: `Campaign mapping approved successfully`,
          message: `Mapping for ${campaignText} ${allCampaignsSelected ? 'has' : 'have'} been approved. Your campaigns have been moved to the In Progress tab. Please allow 2-3 hours for changes to take effect.`
        };
      case CampaignType.UNDER_PROCESSING_CAMPAIGN:
        if (action === 'edit') {
          return {
            title: `Campaign mapping modified successfully`,
            message: `The mapping for ${campaignText} has been modified. Changes will take effect in the next 2-3 hours.`
          };
        } else {
          // unmap action
          return {
            title: `Campaigns unmapped successfully`,
            message: `${campaignText} ${allCampaignsSelected ? 'have' : 'has'} been unmapped. Changes will take effect in the next 2-3 hours.`
          };
        }
      case TaxonomyFeature.AI:
        if (action === 'approveAll') {
          return {
            title: `AI recommendations approved successfully`,
            message: `Mapping for all campaigns under 'AI Recommendations' has been approved. Your campaigns have been moved to the In Progress tab. Please allow 2-3 hours for changes to take effect.`
          };
        } else {
          return {
            title: `AI recommendations approved successfully`,
            message: `Mapping for ${campaignText} ${allCampaignsSelected ? 'has' : 'have'} been approved. Your campaigns have been moved to the In Progress tab. Please allow 2-3 hours for changes to take effect.`
          };
        }
      default:
        return {
          title: `Campaign mapping updated successfully`,
          message: `Your campaigns have been moved to the In Progress tab. Please allow 2-3 hours for changes to take effect.`
        };
    }
  }

  public showCampaignConfirmationDialog(
    campaignType: CampaignType | TaxonomyFeature.AI,
    updateDimensionValues: Record<CampaignDimension, string> & {
      action: TaxonomyAction;
    }
  ): void {
    const { action, ...rest } = updateDimensionValues;
    if (
      action === TaxonomyAction.EDIT &&
      (isEmpty(rest) ||
        Object.values(rest).filter((value) => !isNil(value) && !isEmpty(value))
          .length === 0)
    ) {
      this.toastMessageService.showError(
        'Please select dimensions to update the mapping',
        'No dimensions selected'
      );
      return;
    }
    this.updateDimensionValues = rest;
    this.campaignsConfirmationPopupService.showDialog(
      this.campaignTableService.getSelectedCampaignCount(),
      campaignType,
      this.campaignTableService.allCampaignsSelected,
      action as TaxonomyAction
    );
  }

  public approveCampaignMapping(): void {
    const { campaignType } =
      this.campaignsConfirmationPopupService.confirmationDialog;
    switch (campaignType) {
      case CampaignType.UNMAPPED_CAMPAIGN:
      case CampaignType.MAPPED_CAMPAIGN:
      case CampaignType.UNDER_PROCESSING_CAMPAIGN:
        this.updateCampaignDimensionMapping(this.updateDimensionValues);
        break;
      case TaxonomyFeature.AI:
        this.applyAIRecommendationsMapping(this.updateDimensionValues);
        break;
      default:
        break;
    }
    this.campaignsConfirmationPopupService.hideDialog();
  }

  public cancelCampaignMapping(): void {
    this.campaignTableService.selectedCampaigns = [];
    this.campaignTableService.allCampaignsSelected = false;
    this.campaignTableService.selectedCampaignForAction = null;
    this.campaignsConfirmationPopupService.hideDialog();
  }

  public getCampaignsMappedByAi(): void {
    this.apiService.taxonomyApisOperations
      .getCampaignsMappedByAi(this.clientId, this.brandId)
      .then((response) => {
        this.campaignsMappedByAi = response['count'] || 0;
      })
      .catch((error) => {
        console.error('Error fetching campaigns mapped by AI: ', error);
        this.toastMessageService.showError(
          'Something went wrong!',
          'Failed to fetch campaigns mapped by AI count'
        );
      });
  }

  public cardsToShow(): string[] {
    if (this.activeTaxonomyTab === TaxonomyTab.unmapped) {
      return [TaxonomyFeature.AI, this.activeTaxonomyTab];
    }
    return [this.activeTaxonomyTab];
  }

  public handleTitleClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (target.closest('.ai-learn-more-link')) {
      event.preventDefault();
      event.stopPropagation();
      this.showLearnMoreModal = true;
    }
  }

  public showGeoTestWarning(): boolean {
    if (this.activeTaxonomyTab !== TaxonomyTab.mapped) {
      return false;
    }
    if (this.groupByDimension === TaxonomyDimension.tactic) {
      return !!this.geoTestStatusByDimension[
        this.activeAccordionDimensionValue
      ];
    } else {
      return this.campaignTableService.selectedCampaigns.some(
        (campaign) => !!this.geoTestStatusByDimension[campaign.tactic]
      );
    }
  }

  get columnsForColumnSelector(): IPTableColumnInfo[] {
    return (
      (this.campaignTableService.actualColumns.length > 0
        ? this.campaignTableService.actualColumns
        : this.defaultCampaignTableColumns
      )?.filter(
        (column: IPTableColumnInfo) =>
          !isEmpty(column.field) && !isEmpty(column.header)
      ) || []
    );
  }
}
